/*
 * IC Bundle Builder
 *
 * A single Shopify fixed-bundle parent is submitted through the normal product
 * form. The visual cards never add bundle components with separate requests.
 */
(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-ic-bundle-builder]';

  function parseJson(node, fallback) {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('[IC Bundle Builder] Invalid configuration.', error);
      return fallback;
    }
  }

  function normalize(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  function parseValueMap(value) {
    const map = new Map();
    String(value || '')
      .split(/[\n,;]/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const separator = entry.includes('=') ? '=' : entry.includes(':') ? ':' : '';
        if (!separator) return;
        const parts = entry.split(separator).map((part) => normalize(part));
        if (parts.length >= 2 && parts[0] && parts[1]) map.set(parts[0], parts[1]);
      });
    return map;
  }

  function firstAvailable(variants) {
    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function matchBundleVariant(config, sourceData, sourceVariant) {
    const variants = Array.isArray(config.variants) ? config.variants : [];
    if (!variants.length) return null;

    const fallback = firstAvailable(variants);
    if (config.variantMode === 'first_available' || !sourceVariant) return fallback;

    const sourceNames = Array.isArray(sourceData.optionNames) ? sourceData.optionNames : [];
    const targetNames = Array.isArray(config.optionNames) ? config.optionNames : [];
    const map = parseValueMap(config.variantMap);
    const sharedOptions = [];

    sourceNames.forEach((sourceName, sourceIndex) => {
      const targetIndex = targetNames.findIndex((targetName) => normalize(targetName) === normalize(sourceName));
      const sourceValue = normalize(sourceVariant.options?.[sourceIndex]);
      if (targetIndex < 0 || !sourceValue || sourceValue === 'default title') return;
      sharedOptions.push({ targetIndex, value: map.get(sourceValue) || sourceValue });
    });

    if (!sharedOptions.length) return fallback;

    const exactMatches = variants.filter((variant) =>
      sharedOptions.every(({ targetIndex, value }) => normalize(variant.options?.[targetIndex]) === value)
    );

    return firstAvailable(exactMatches);
  }

  function initBuilder(root) {
    if (!root || root.dataset.icBundleBuilderReady === 'true') return;

    const sourceData = parseJson(root.querySelector('[data-ic-bundle-source-data]'), {
      optionNames: [],
      variants: []
    });
    const cards = Array.from(root.querySelectorAll('[data-ic-bundle-card]'))
      .map((wrapper) => ({
        wrapper,
        input: wrapper.querySelector('[data-ic-bundle-choice]'),
        label: wrapper.querySelector('[data-ic-bundle-label]'),
        config: parseJson(wrapper.querySelector('[data-ic-bundle-card-config]'), null)
      }))
      .filter((card) => card.input && card.label && card.config && Array.isArray(card.config.variants));

    if (!cards.length) return;
    root.dataset.icBundleBuilderReady = 'true';

    const sectionId = root.dataset.sectionId;
    const formIds = [
      `product-form-${sectionId}`,
      `product-form-installment-${sectionId}`,
      `product-form-sticky-${sectionId}`
    ];
    const state = {
      key: root.dataset.defaultOffer || cards[0].config.key,
      card: null,
      variant: null
    };

    function sourceVariant() {
      const form = document.getElementById(`product-form-${sectionId}`);
      const input = form?.querySelector('input[name="id"]:not([data-ic-bundle-submit-id])');
      const id = String(input?.value || '');
      return sourceData.variants.find((variant) => String(variant.id) === id) || sourceData.variants[0] || null;
    }

    function setCardAppearance(card, variant, selected) {
      const unavailable = !variant || !variant.available;
      const price = card.wrapper.querySelector('[data-ic-bundle-card-price]');
      const compare = card.wrapper.querySelector('[data-ic-bundle-card-compare]');
      const image = card.wrapper.querySelector('[data-ic-bundle-card-image]');
      const usesCustomPrice = card.config.priceMode === 'custom';
      const customPriceLabel = String(card.config.customPriceLabel || '').trim();
      const customCompareLabel = String(card.config.customCompareLabel || '').trim();

      card.input.checked = selected;
      card.input.disabled = unavailable;
      card.label.classList.toggle('is-unavailable', unavailable);
      card.label.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      const showCurrency = root.dataset.icShowCurrency === 'true';
      const priceLabel = showCurrency ? variant?.priceLabelWithCurrency : variant?.priceLabel;
      const variantCompareLabel = showCurrency ? variant?.compareAtLabelWithCurrency : variant?.compareAtLabel;

      if (price) price.textContent = usesCustomPrice && customPriceLabel ? customPriceLabel : (priceLabel || '');
      if (compare) {
        const compareLabel = usesCustomPrice ? customCompareLabel : (variantCompareLabel || '');
        compare.textContent = compareLabel;
        compare.hidden = !compareLabel;
      }
      if (image?.dataset.icBundleAutoImage === 'true' && variant?.image) image.src = variant.image;
    }

    function displayMoney(variant, priceNode, type) {
      if (!variant) return '';
      const showCurrency = priceNode?.dataset.icShowCurrency === 'true';
      const key = type === 'compare'
        ? (showCurrency ? 'compareAtLabelWithCurrency' : 'compareAtLabel')
        : (showCurrency ? 'priceLabelWithCurrency' : 'priceLabel');
      return String(variant[key] || '');
    }

    function updateOfferPrice(variant) {
      const priceNode = document.querySelector(`#price-${sectionId} [data-ic-offer-price]`);
      if (!priceNode || !variant) return;

      const current = priceNode.querySelector('[data-ic-price-current]');
      const compare = priceNode.querySelector('[data-ic-price-compare]');
      const regular = priceNode.querySelector('[data-ic-price-regular]');
      const currentCents = priceNode.querySelector('.target-price');
      const compareCents = priceNode.querySelector('.target-compare-at-price');
      const currency = priceNode.querySelector('[data-ic-price-currency]');
      const hasCompare = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);

      // The deal layout renders the ISO code in its own element. Keep the
      // monetary value itself code-free here so selecting an offer does not
      // create a duplicated "USD USD" label.
      if (current) current.textContent = String(variant.priceLabel || '');
      if (compare) compare.textContent = hasCompare ? String(variant.compareAtLabel || '') : '';
      if (regular) regular.hidden = !hasCompare;
      if (currentCents) currentCents.textContent = String(variant.price || '');
      if (compareCents) compareCents.textContent = String(hasCompare ? variant.compareAtPrice : '');
      if (currency) currency.textContent = variant.currencyCode || currency.textContent || '';
    }

    function updateStandardPrice(variant) {
      const priceNode = document.querySelector(`#price-${sectionId} [data-ic-standard-price]`);
      if (!priceNode || !variant) return;

      const current = displayMoney(variant, priceNode, 'price');
      const compare = displayMoney(variant, priceNode, 'compare');
      const hasCompare = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
      const priceClass = 'price-sale selection:bg-text-[rgb(var(--colors-price-sale),0.2)] ml-3 inline-flex items-baseline flex-wrap leading-none';

      priceNode.innerHTML = [
        '<div>',
        `<div class="target-price hidden">${String(variant.price || '')}</div>`,
        `<div class="target-compare-at-price hidden">${String(hasCompare ? variant.compareAtPrice : '')}</div>`,
        hasCompare
          ? `<div class="flex items-baseline flex-wrap"><small class="cap rtl:inline-block"><s class="price-compare rtl:leading-tight">${compare}</s></small><span class="${priceClass}"><span>${current}</span></span></div>`
          : `<p class="price"><span class="inline-flex items-baseline flex-wrap leading-none"><span>${current}</span></span></p>`,
        '</div>'
      ].join('');
    }

    function updateMainPrice(variant) {
      updateOfferPrice(variant);
      updateStandardPrice(variant);
    }

    function setButtons(variant) {
      formIds.forEach((formId) => {
        const form = document.getElementById(formId);
        if (!form) return;
        form.querySelectorAll('.add_to_cart_button').forEach((button) => {
          const available = Boolean(variant?.available);
          button.disabled = !available;
          button.dataset.available = available ? 'true' : 'false';
        });
      });
    }

    function syncForms() {
      const valid = Boolean(state.card && state.variant?.available);
      const properties = state.card?.config?.properties || {};

      formIds.forEach((formId) => {
        const form = document.getElementById(formId);
        const parentIdInput = root.querySelector(`[data-ic-bundle-submit-id][form="${formId}"]`);
        const sourceIdInput = form?.querySelector('input[name="id"]:not([data-ic-bundle-submit-id])');

        if (sourceIdInput) sourceIdInput.disabled = true;
        if (parentIdInput) {
          parentIdInput.disabled = !valid;
          parentIdInput.value = valid ? String(state.variant.id) : '';
        }
        root.querySelectorAll(`[data-ic-bundle-property][form="${formId}"]`).forEach((input) => {
          input.disabled = !valid;
          input.value = valid ? (properties[input.dataset.icBundleProperty] || '') : '';
        });
      });

      setButtons(valid ? state.variant : null);
    }

    function resolveState() {
      const activeSourceVariant = sourceVariant();
      const variantsByCard = cards.map((card) => ({
        card,
        variant: matchBundleVariant(card.config, sourceData, activeSourceVariant)
      }));
      let selected = variantsByCard.find(({ card }) => card.config.key === state.key) || variantsByCard[0];

      if (!selected.variant?.available) {
        selected = variantsByCard.find(({ variant }) => variant?.available) || selected;
      }

      state.card = selected.card;
      state.key = selected.card.config.key;
      state.variant = selected.variant;

      variantsByCard.forEach(({ card, variant }) => {
        setCardAppearance(card, variant, card === state.card);
      });
      syncForms();
      updateMainPrice(state.variant);

      root.dispatchEvent(new CustomEvent('ic:bundle-builder:updated', {
        bubbles: true,
        detail: { offer: state.card.config, variant: state.variant }
      }));
    }

    function scheduleResolve() {
      window.requestAnimationFrame(resolveState);
    }

    cards.forEach((card) => {
      card.input.addEventListener('change', () => {
        if (!card.input.checked || card.input.disabled) return;
        state.key = card.config.key;
        resolveState();
      });

      card.label.addEventListener('click', (event) => {
        if (card.input.disabled) {
          event.preventDefault();
        }
      });
    });

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (input instanceof HTMLInputElement && input.id === `update-variant-${sectionId}`) scheduleResolve();
    });
    document.addEventListener(`eurus:product-page-variant-select:updated:${sectionId}`, scheduleResolve);
    document.addEventListener(`eurus:product-card-variant-select:updated:${sectionId}`, scheduleResolve);
    document.addEventListener('click', (event) => {
      const button = event.target.closest(`#x-atc-button-${sectionId}, #x-atc-button-sticky-${sectionId}`);
      if (button) syncForms();
    }, true);
    document.addEventListener('submit', (event) => {
      if (formIds.includes(event.target?.id)) syncForms();
    }, true);

    if (root.dataset.hideMainPrice === 'true') {
      const priceBlock = document.getElementById(`price-${sectionId}`)?.closest('.x-block-price');
      if (priceBlock) priceBlock.classList.add('ic-bundle-builder-price-hidden');
    }

    resolveState();
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
